import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

import useFinancingForm from '../../../../hooks/formControllers/esl/useFinancingForm';
import useAlert from '../../../../hooks/useAlert';
import useMobileDetect from '../../../../hooks/useMobileDetect';
import { isCustomError } from '../../../../lib/isCustomError';
import { createSubmitHandler } from '../../../../lib/submitHandler';
import { commonApplicationProfileAtom } from '../../../../state/atoms/commonApplicationProfile';
import financingForm from '../../../../state/forms/eslForms/financingForm';
import { ValidationError } from '../../../../types/errors/validation';
import { RequireAdditionalFinancingOption } from '../../../../types/eslProfileFinancingForm';
import { ProfileCompletionStepComponentBaseProps } from '../../../../types/ProfileCompletionStep';
import PreApprovalSingleChoiceQuestion from '../../../esl/profileSections/financing/questions/singleChoiceQuestion/PreApprovalSingleChoiceQuestion';
import ProfilePopUpButtons from '../ProfilePopUpButtons';

const requireAdditionalFinancingOptionRecord: Record<
  RequireAdditionalFinancingOption,
  string
> = {
  [RequireAdditionalFinancingOption.YES]: 'Yes',
  [RequireAdditionalFinancingOption.NO]: 'No',
  [RequireAdditionalFinancingOption.NOT_SURE]: 'Unsure',
};

const yesNoQuestionOptionRecord: Record<'true' | 'false', string> = {
  true: 'Yes',
  false: 'No',
};

type FinancingInfoFormStepProps = ProfileCompletionStepComponentBaseProps;

// TODO: Refactor form fields into separate components
const FinancingInfoFormStep: FunctionComponent<FinancingInfoFormStepProps> = ({
  closeModal,
  description,
  navigateNext,
  navigatePrevious,
  title,
}) => {
  const { submitForm } = useFinancingForm();
  const { showSuccessAlert, showErrorAlert } = useAlert();
  const [ isContinueLoading, setIsContinueLoading ] = useState(false);
  const [ isSaveAndExitLoading, setIsSaveAndExitLoading ] = useState(false);

  const [ commonApplicationProfile, setCommonApplicationProfile ]
    = useRecoilState(commonApplicationProfileAtom);
  const isMobile = useMobileDetect();

  const existingStudentLoansValid = useRecoilValue(
    financingForm.fields.existingStudentLoans.validState,
  );

  const additionalFinancingValid = useRecoilValue(
    financingForm.fields.additionalFinancing.validState,
  );

  const setExistingStudentLoans = useSetRecoilState(
    financingForm.fields.existingStudentLoans.valueState,
  );

  const setAdditionalFinancing = useSetRecoilState(
    financingForm.fields.additionalFinancing.valueState,
  );

  // Populate fields from the common application profile data
  useEffect(() => {
    setExistingStudentLoans(
      commonApplicationProfile?.existing_student_loans ?? null,
    );

    setAdditionalFinancing(
      commonApplicationProfile?.additional_financing ?? null,
    );
  }, [
    commonApplicationProfile,
    setExistingStudentLoans,
    setAdditionalFinancing,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      const newCommonApplicationProfile = await submitForm();
      setCommonApplicationProfile(newCommonApplicationProfile);
      showSuccessAlert('Profile financing info updated');

      return true;
    } catch (err) {
      console.error(err);

      if (err instanceof ValidationError) {
        showErrorAlert(err.message);
      } else {
        if (isCustomError(err)) {
          const errorMessage = err?.response?.data?.detail;

          showErrorAlert(
            errorMessage
              ? errorMessage
              : `Unsuccessful profile financing info update. Details: ${err}`,
          );
        } else {
          showErrorAlert('Unknown error occurred.');
        }
      }

      return false;
    }
  }, [
    setCommonApplicationProfile,
    showErrorAlert,
    showSuccessAlert,
    submitForm,
  ]);

  return (
    <Box id='financing-info-form'>
      <DialogTitle id='financing-info-title'>
        <Typography align='center' variant={isMobile ? 'h3' : 'h2'}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mb: 4 }}>
        <DialogContentText id='financing-info-description'>
          <Typography align='center' variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            {description}
          </Typography>
        </DialogContentText>
        <Grid container direction={'row'} mt={2} spacing={3}>
          <Grid item xs={12}>
            <PreApprovalSingleChoiceQuestion
              data={yesNoQuestionOptionRecord}
              error={!existingStudentLoansValid}
              field={financingForm.fields.existingStudentLoans}
              title='Do you have existing student loans that you want to your employer to make payments towards?'
            />
          </Grid>
          <Grid item xs={12}>
            <PreApprovalSingleChoiceQuestion
              data={requireAdditionalFinancingOptionRecord}
              error={!additionalFinancingValid}
              field={financingForm.fields.additionalFinancing}
              title='Do you require any additional financing to complete your degree program?'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <ProfilePopUpButtons
          disabled={isContinueLoading || isSaveAndExitLoading}
          loadingContinue={isContinueLoading}
          loadingSaveAndExit={isSaveAndExitLoading}
          onBack={navigatePrevious}
          onContinue={
            createSubmitHandler({
              startLoadingState: () => setIsContinueLoading(true),
              handleSubmit,
              onSuccess: () => navigateNext(),
              onFinally: () => setIsContinueLoading(false),
            })
          }
          onSaveAndExit={
            createSubmitHandler({
              startLoadingState: () => setIsSaveAndExitLoading(true),
              handleSubmit,
              onSuccess: () => closeModal(),
              onFinally: () => setIsSaveAndExitLoading(false),
            })
          }
          showBack={true}
          showContinue={true}
          showSaveAndExit={isMobile}
        />
      </DialogActions>
    </Box>
  );
};

export default FinancingInfoFormStep;
